import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Divider,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Store as StoreIcon,
  Place as PlaceIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  AccessTime as AccessTimeIcon,
  Restaurant as RestaurantIcon,
  MeetingRoom as EventSpaceIcon,
  Hotel as ResortIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  LocalOffer as PackageIcon,
  Receipt as PurchaseIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { useVenue } from '../../contexts/VenueContext';
import { Venue, VenueType, Package, BusinessHours } from '../../types/Venue';
import { getPackagesByVenueId } from '../../services/venueService';
import { format } from 'date-fns';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  Timestamp, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { PackageBooking } from '../../services/bookingService';

// Tab panel component for the tabbed interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`venue-tabpanel-${index}`}
      aria-labelledby={`venue-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `venue-tab-${index}`,
    'aria-controls': `venue-tabpanel-${index}`,
  };
};

const VenueDetail: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const { venues, activeVenue, setActiveVenue, isLoading, error } = useVenue();
  const [tabValue, setTabValue] = useState(0);
  const [packages, setPackages] = useState<Package[]>([]);
  const [purchases, setPurchases] = useState<PackageBooking[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [loadingPurchases, setLoadingPurchases] = useState(false);

  useEffect(() => {
    if (venueId) {
      setActiveVenue(venueId);
      fetchPackages(venueId);
    }
  }, [venueId]);

  const fetchPackages = async (id: string) => {
    setLoadingPackages(true);
    try {
      const venuePackages = await getPackagesByVenueId(id);
      setPackages(venuePackages);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getVenueTypeIcon = (type: VenueType) => {
    switch (type) {
      case 'restaurant':
        return <RestaurantIcon sx={{ fontSize: 40 }} />;
      case 'event_space':
        return <EventSpaceIcon sx={{ fontSize: 40 }} />;
      case 'resort':
        return <ResortIcon sx={{ fontSize: 40 }} />;
      default:
        return <StoreIcon sx={{ fontSize: 40 }} />;
    }
  };

  const getVenueTypeLabel = (type: VenueType) => {
    switch (type) {
      case 'restaurant':
        return 'Restaurant';
      case 'event_space':
        return 'Event Space';
      case 'resort':
        return 'Resort';
      default:
        return 'Venue';
    }
  };

  const getVenueImage = (venue: Venue) => {
    const fallbackImages = {
      restaurant: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      event_space: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      resort: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
    };
    
    if (venue?.images?.primary && venue.images.primary.startsWith('http')) {
      return venue.images.primary;
    }
    
    return fallbackImages[venue?.type || 'restaurant'];
  };

  const formatDayOfWeek = (day: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day];
  };

  const renderBusinessHours = (businessHours: BusinessHours[]) => {
    return businessHours.map((hours, index) => (
      <ListItem key={index} sx={{ py: 0.5 }}>
        <ListItemIcon sx={{ minWidth: 40 }}>
          <AccessTimeIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">{formatDayOfWeek(hours.dayOfWeek)}</Typography>
              <Typography variant="body2" color={hours.isClosed ? 'text.secondary' : 'text.primary'}>
                {hours.isClosed ? 'Closed' : `${hours.open} - ${hours.close}`}
              </Typography>
            </Box>
          } 
        />
      </ListItem>
    ));
  };

  const handleCreatePackage = () => {
    navigate(`/venues/${venueId}/packages/create`);
  };

  const handleGenerateTestBookings = async () => {
    if (!activeVenue || !activeVenue.id) {
      alert('Please select a venue first');
      return;
    }

    // Confirm before generating test data
    if (!window.confirm('This will generate 5 test bookings for this venue. Continue?')) {
      return;
    }

    try {
      // Get packages for this venue to use in test bookings
      const venuePackages = await getPackagesByVenueId(activeVenue.id);
      
      if (venuePackages.length === 0) {
        alert('Please create at least one package for this venue before generating test bookings');
        return;
      }

      // Set up booking dates (1 past, 1 today, 3 future)
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);
      
      const nextMonth = new Date(today);
      nextMonth.setDate(nextMonth.getDate() + 30);

      // Test customers
      const testCustomers = [
        {
          userId: 'test-user-1',
          userName: 'John Smith',
          userEmail: 'john.smith@example.com'
        },
        {
          userId: 'test-user-2',
          userName: 'Jane Doe',
          userEmail: 'jane.doe@example.com'
        },
        {
          userId: 'test-user-3',
          userName: 'Bob Johnson',
          userEmail: 'bob.johnson@example.com'
        }
      ];

      // Statuses
      const statuses = ['confirmed', 'completed', 'cancelled'];
      const timeSlots = ['10:00 - 12:00', '12:00 - 14:00', '18:00 - 20:00', '19:00 - 21:00'];

      // Create an array of 5 test bookings
      const testBookings = [
        // Past booking (completed)
        {
          packageId: venuePackages[0].id,
          packageName: venuePackages[0].name,
          venueId: activeVenue.id,
          venueName: activeVenue.name,
          userId: testCustomers[0].userId,
          userEmail: testCustomers[0].userEmail,
          userName: testCustomers[0].userName,
          bookingDate: yesterday,
          timeSlot: {
            start: timeSlots[0].split(' - ')[0],
            end: timeSlots[0].split(' - ')[1]
          },
          guests: 2,
          amount: venuePackages[0].price * 1.08,
          status: 'completed',
          paymentId: 'pmt_test_' + Math.random().toString(36).substring(2, 10),
          specialRequests: 'Please prepare a table near the window.',
          redemptionCode: 'TEST-' + Math.random().toString(36).substring(2, 6).toUpperCase(),
          createdAt: new Date(yesterday.getTime() - 86400000), // 1 day before the booking
          updatedAt: yesterday,
          redeemedAt: yesterday
        },
        
        // Today's booking (confirmed)
        {
          packageId: venuePackages[0].id,
          packageName: venuePackages[0].name,
          venueId: activeVenue.id,
          venueName: activeVenue.name,
          userId: testCustomers[1].userId,
          userEmail: testCustomers[1].userEmail,
          userName: testCustomers[1].userName,
          bookingDate: today,
          timeSlot: {
            start: timeSlots[1].split(' - ')[0],
            end: timeSlots[1].split(' - ')[1]
          },
          guests: 3,
          amount: venuePackages[0].price * 1.08,
          status: 'confirmed',
          paymentId: 'pmt_test_' + Math.random().toString(36).substring(2, 10),
          specialRequests: 'Celebrating a birthday, please add a candle.',
          redemptionCode: 'TEST-' + Math.random().toString(36).substring(2, 6).toUpperCase(),
          createdAt: new Date(today.getTime() - 86400000), // 1 day before
          updatedAt: today
        },
        
        // Tomorrow's booking (confirmed)
        {
          packageId: venuePackages[1 % venuePackages.length].id,
          packageName: venuePackages[1 % venuePackages.length].name,
          venueId: activeVenue.id,
          venueName: activeVenue.name,
          userId: testCustomers[2].userId,
          userEmail: testCustomers[2].userEmail,
          userName: testCustomers[2].userName,
          bookingDate: tomorrow,
          timeSlot: {
            start: timeSlots[2].split(' - ')[0],
            end: timeSlots[2].split(' - ')[1]
          },
          guests: 4,
          amount: venuePackages[1 % venuePackages.length].price * 1.08,
          status: 'confirmed',
          paymentId: 'pmt_test_' + Math.random().toString(36).substring(2, 10),
          redemptionCode: 'TEST-' + Math.random().toString(36).substring(2, 6).toUpperCase(),
          createdAt: today,
          updatedAt: today
        },
        
        // Next week booking (confirmed)
        {
          packageId: venuePackages[2 % venuePackages.length].id,
          packageName: venuePackages[2 % venuePackages.length].name,
          venueId: activeVenue.id,
          venueName: activeVenue.name,
          userId: testCustomers[0].userId,
          userEmail: testCustomers[0].userEmail,
          userName: testCustomers[0].userName,
          bookingDate: nextWeek,
          timeSlot: {
            start: timeSlots[3].split(' - ')[0],
            end: timeSlots[3].split(' - ')[1]
          },
          guests: 5,
          amount: venuePackages[2 % venuePackages.length].price * 1.08,
          status: 'confirmed',
          paymentId: 'pmt_test_' + Math.random().toString(36).substring(2, 10),
          specialRequests: 'Vegan menu options required.',
          redemptionCode: 'TEST-' + Math.random().toString(36).substring(2, 6).toUpperCase(),
          createdAt: today,
          updatedAt: today
        },
        
        // Cancelled booking
        {
          packageId: venuePackages[0].id,
          packageName: venuePackages[0].name,
          venueId: activeVenue.id,
          venueName: activeVenue.name,
          userId: testCustomers[1].userId,
          userEmail: testCustomers[1].userEmail,
          userName: testCustomers[1].userName,
          bookingDate: nextMonth,
          timeSlot: {
            start: timeSlots[0].split(' - ')[0],
            end: timeSlots[0].split(' - ')[1]
          },
          guests: 2,
          amount: venuePackages[0].price * 1.08,
          status: 'cancelled',
          paymentId: 'pmt_test_' + Math.random().toString(36).substring(2, 10),
          redemptionCode: 'TEST-' + Math.random().toString(36).substring(2, 6).toUpperCase(),
          createdAt: new Date(today.getTime() - 3 * 86400000), // 3 days ago
          updatedAt: new Date(today.getTime() - 1 * 86400000), // 1 day ago
          cancelledAt: new Date(today.getTime() - 1 * 86400000) // 1 day ago
        }
      ];

      // Add test bookings to the database
      const results = await Promise.all(
        testBookings.map(async (booking) => {
          // Convert dates to Firestore Timestamps
          const firestoreBooking = {
            ...booking,
            bookingDate: Timestamp.fromDate(booking.bookingDate),
            createdAt: Timestamp.fromDate(booking.createdAt),
            updatedAt: Timestamp.fromDate(booking.updatedAt),
            redeemedAt: booking.redeemedAt ? Timestamp.fromDate(booking.redeemedAt) : null,
            cancelledAt: booking.cancelledAt ? Timestamp.fromDate(booking.cancelledAt) : null
          };

          // Add to Firestore
          const bookingRef = await addDoc(collection(db, 'packageBookings'), firestoreBooking);
          return bookingRef.id;
        })
      );

      alert(`Successfully created ${results.length} test bookings!`);
      
      // Refresh purchases
      setPurchases([]);
      fetchVenuePurchases(activeVenue.id);
      
    } catch (error) {
      console.error('Error generating test bookings:', error);
      alert('Error generating test bookings. See console for details.');
    }
  };

  // Function to fetch purchases/bookings for a venue
  const fetchVenuePurchases = async (venueId: string) => {
    if (!venueId) return;
    
    setLoadingPurchases(true);
    try {
      const bookingsQuery = query(
        collection(db, 'packageBookings'),
        where('venueId', '==', venueId),
        orderBy('createdAt', 'desc')
      );
      
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingsList: PackageBooking[] = [];
      
      bookingsSnapshot.forEach((doc) => {
        bookingsList.push({
          id: doc.id,
          ...doc.data()
        } as PackageBooking);
      });
      
      setPurchases(bookingsList);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoadingPurchases(false);
    }
  };
  
  // Fetch purchases when venue loads
  useEffect(() => {
    if (activeVenue?.id) {
      fetchVenuePurchases(activeVenue.id);
    }
  }, [activeVenue]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/venues')}
          sx={{ mt: 2 }}
        >
          Back to Venues
        </Button>
      </Container>
    );
  }

  if (!activeVenue) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Venue not found</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/venues')}
          sx={{ mt: 2 }}
        >
          Back to Venues
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header with back button and edit button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/venues')}
        >
          Back to Venues
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<EditIcon />}
          onClick={() => navigate(`/venues/${venueId}/edit`)}
        >
          Edit Venue
        </Button>
      </Box>

      {/* Venue Hero Section */}
      <Paper elevation={2} sx={{ mb: 4, overflow: 'hidden' }}>
        <Box sx={{ position: 'relative' }}>
          {/* Hero Image */}
          <Box 
            sx={{ 
              height: 300, 
              backgroundImage: `url(${getVenueImage(activeVenue)})`, 
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.3)',
                zIndex: 1
              }
            }}
          />
          
          {/* Venue Logo */}
          {activeVenue.images?.logo && (
            <Avatar
              src={activeVenue.images.logo}
              sx={{
                width: 100,
                height: 100,
                border: '4px solid white',
                position: 'absolute',
                bottom: -50,
                left: 30,
                zIndex: 2,
                boxShadow: 2
              }}
            />
          )}
          
          {/* Venue Type Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              bgcolor: 'background.paper',
              borderRadius: 2,
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: 2,
              zIndex: 2
            }}
          >
            {getVenueTypeIcon(activeVenue.type)}
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
              {getVenueTypeLabel(activeVenue.type)}
            </Typography>
          </Box>
        </Box>
        
        {/* Venue Details */}
        <Box sx={{ p: 3, pt: activeVenue.images?.logo ? 7 : 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {activeVenue.name}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {activeVenue.description}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PlaceIcon sx={{ color: 'text.secondary', mr: 1 }} />
                <Typography variant="body1">
                  {activeVenue.location.address}, {activeVenue.location.city}, {activeVenue.location.state}, {activeVenue.location.postalCode}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon sx={{ color: 'text.secondary', mr: 1 }} />
                <Typography variant="body1">
                  {activeVenue.contact.phone}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon sx={{ color: 'text.secondary', mr: 1 }} />
                <Typography variant="body1">
                  {activeVenue.contact.email}
                </Typography>
              </Box>
              
              {activeVenue.contact.website && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WebsiteIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  <Typography variant="body1">
                    <a href={activeVenue.contact.website} target="_blank" rel="noopener noreferrer">
                      {activeVenue.contact.website}
                    </a>
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Business Hours
              </Typography>
              <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                {renderBusinessHours(activeVenue.businessHours)}
              </List>
            </Grid>
          </Grid>
          
          {/* Social Media Links */}
          {activeVenue.socialMedia && Object.values(activeVenue.socialMedia).some(link => link) && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Follow Us
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {activeVenue.socialMedia.facebook && (
                  <IconButton color="primary" component="a" href={activeVenue.socialMedia.facebook} target="_blank">
                    <FacebookIcon />
                  </IconButton>
                )}
                {activeVenue.socialMedia.instagram && (
                  <IconButton color="primary" component="a" href={activeVenue.socialMedia.instagram} target="_blank">
                    <InstagramIcon />
                  </IconButton>
                )}
                {activeVenue.socialMedia.twitter && (
                  <IconButton color="primary" component="a" href={activeVenue.socialMedia.twitter} target="_blank">
                    <TwitterIcon />
                  </IconButton>
                )}
                {activeVenue.socialMedia.linkedin && (
                  <IconButton color="primary" component="a" href={activeVenue.socialMedia.linkedin} target="_blank">
                    <LinkedInIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Tabs Navigation */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="venue tabs"
          variant="fullWidth"
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab label="Packages" {...a11yProps(1)} />
          <Tab label="Purchases & Redemptions" {...a11yProps(2)} />
        </Tabs>
        
        {/* Tab Content */}
        <Box sx={{ px: 3 }}>
          {/* Overview Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={4}>
              {/* Amenities Section */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Amenities
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                  {activeVenue.amenities.map((amenity, index) => (
                    <Chip key={index} label={amenity} variant="outlined" />
                  ))}
                </Box>
              </Grid>
              
              {/* Gallery Section */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Gallery
                  </Typography>
                </Box>
                <Grid container spacing={1}>
                  {activeVenue.images?.gallery?.length > 0 ? (
                    activeVenue.images.gallery.map((image, index) => (
                      <Grid item xs={6} sm={4} key={index}>
                        <Card sx={{ height: '100%' }}>
                          <CardMedia
                            component="img"
                            image={image}
                            alt={`Gallery image ${index + 1}`}
                            sx={{ height: 120, objectFit: 'cover' }}
                          />
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'background.default' }}>
                        <Typography variant="body2" color="text.secondary">
                          No gallery images available
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Packages Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Date Packages
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreatePackage}
              >
                Create Package
              </Button>
            </Box>
            
            {loadingPackages ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : packages.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
                <PackageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No packages yet
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Create your first date package to start offering experiences to customers.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleCreatePackage}
                  sx={{ mt: 2 }}
                >
                  Create Your First Package
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {packages.map((pkg) => (
                  <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                    <Card sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={pkg.images?.[0] || getVenueImage(activeVenue)}
                        alt={pkg.name}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {pkg.name}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="h6" color="primary" fontWeight="bold">
                            ${pkg.price.toFixed(2)}
                          </Typography>
                          {pkg.discountedPrice && (
                            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              ${pkg.discountedPrice.toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {pkg.description}
                        </Typography>
                        
                        <Typography variant="subtitle2" gutterBottom>
                          Includes:
                        </Typography>
                        <List dense disablePadding>
                          {pkg.includesItems.map((item, i) => (
                            <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                      <Divider />
                      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/venues/${venueId}/packages/${pkg.id}`)}
                        >
                          View Details
                        </Button>
                        <Chip 
                          label={pkg.isActive ? "Active" : "Inactive"} 
                          color={pkg.isActive ? "success" : "default"}
                          size="small"
                        />
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </TabPanel>
          
          {/* Purchases & Redemptions Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Purchase History & Redemptions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<QrCodeIcon />}
                  onClick={() => navigate('/venues/bookings')}
                >
                  Manage Bookings
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={handleGenerateTestBookings}
                >
                  Generate Test Bookings
                </Button>
              </Box>
            </Box>
            
            {purchases.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
                <PurchaseIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No purchases yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  When customers purchase your packages, they will appear here.
                </Typography>
              </Paper>
            ) : (
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Box sx={{ p: 2 }}>
                  {/* Purchase table would go here */}
                  <Typography>Purchase history will be implemented in the next phase</Typography>
                </Box>
              </Paper>
            )}
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                QR Code Redemption
              </Typography>
              <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <QrCodeIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6">Scan QR Codes</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use QR code scanner to redeem customer purchases
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(`/venues/${venueId}/qr-scanner`)}
                >
                  Open Scanner
                </Button>
              </Paper>
            </Box>
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default VenueDetail;
